// editorjs/tools/columns-block.js

import {v4 as uuidv4} from "uuid";
import Swal from "sweetalert2";
import {make, stopPropagationOnEditorJsControlKeys} from "@/editorjs/utils.js";

export default class Columns {

    static get enableLineBreaks() {
        return true;
    }

    constructor({data, config, api, readOnly, block}) {
        this.api = api;
        this.readOnly = readOnly;
        this.config = config || {};
        this.block = block;
        this.blockId = this.block.id;

        this.editors = {
            cols: [],
        };
        this.colWrapper = undefined;

        this.isNewBlock = !data?.cols?.length;
        this.keydownHandlers = new Map();

        // data를 초기화합니다 - 깊은 복사로 독립성 보장
        this.data = this._ensureDataIndependence(data || {});

        // ✨ [핵심 수정] 새로운 블록일 경우, 2열로 기본값을 강제 설정합니다.
        if (this.isNewBlock) {
            this.data.cols = [
                { blocks: [] }, // 1열 데이터
                { blocks: [] }  // 2열 데이터
            ];
            this.data.style = {};
            this.data.gap = this._getDefaultGap;
            this.data.ratios = this._getDefaultLayout(2); // 2열 비율 [1, 1]
        } else {
            // 기존에 데이터가 있을 경우, 안전하게 기본값을 채웁니다.
            this.data.cols = this.data.cols || [];
            this.data.style = this.data.style || {};
            this.data.gap = this.data.gap ?? this._getDefaultGap;
            // ratios가 컬럼 수와 맞지 않는 경우를 대비해 재설정
            if (this.data.ratios?.length !== this.data.cols.length) {
                this.data.ratios = this._getDefaultLayout(this.data.cols.length);
            }
        }

        // 이제 data.cols의 길이를 기준으로 numberOfColumns를 설정하면 항상 정확합니다.
        this.editors.numberOfColumns = this.data.cols.length;

        if (!this.readOnly) {
            this.onKeyUp = this.onKeyUp.bind(this);
        }
    }

    destroy() {
        if (this.config.unregisterUndoManager) {
            this.config.unregisterUndoManager(this.blockId);
        }

        this._destroyAllEditors();
    }

    _destroyAllEditors() {
        this.editors.cols.forEach(editor => {
            if (editor && typeof editor.destroy === 'function') {
                const editorWrapper = editor.ui.nodes.wrapper;
                const keydownHandler = this.keydownHandlers.get(editor);

                if (editorWrapper) {
                    if (keydownHandler) {
                        editorWrapper.removeEventListener('keydown', keydownHandler);
                    }
                }

                this.keydownHandlers.delete(editor);
                editor.destroy();
            }
        });
        this.editors.cols = [];
    }

    static get isReadOnlySupported() {
        return true;
    }

    onKeyUp(e) {
        if (e.code !== "Backspace" && e.code !== "Delete") {
            return;
        }
    }

    /**
     * 데이터 독립성을 보장하는 헬퍼 함수
     * 각 columns 인스턴스가 완전히 독립적인 데이터를 갖도록 함
     */
    _ensureDataIndependence(data) {
        // 깊은 복사를 통해 참조 공유 방지
        if (data.cols) {
            return {
                ...data,
                cols: data.cols.map(col => ({
                    blocks: col.blocks ? JSON.parse(JSON.stringify(col.blocks)) : []
                })),
                style: data.style ? {...data.style} : {},
                gap: data.gap ?? this._getDefaultGap,
                ratios: data.ratios ? [...data.ratios] : []
            };
        }
        return data;
    }

    // Helper to get default layout based on column count
    _getDefaultLayout(numCols) {
        return new Array(numCols).fill(1);
    }

    get _getDefaultGap() {
        return 10;
    }

    renderSettings() {
        // 기존 설정 아이템
        const settingsItems = this._listColumnsSettingItems();

        // Config 아이콘 (기본)
        const configIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-14a1 1 0 0 1 1 -1z" /><path d="M15 12l4 0" /><path d="M17 10l0 4" /></svg>`;

        // 최종 반환 배열
        return [
            {
                icon: configIcon,
                title: this.api.i18n.t('Config'),
                children: { items: settingsItems },
                closeOnActivate: false
            }
        ];
    }

    _listColumnsSettingItems() {
        const columnSettings = [
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z" /></svg>`,
                title: this.api.i18n.t("1 Column"),
                onActivate: () => {
                    this._updateCols(1)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm9 -1v18" /></svg>`,
                title: this.api.i18n.t("2 Columns"),
                onActivate: () => {
                    this._updateCols(2)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm6 -1v18m6 -18v18" /></svg>`,
                title: this.api.i18n.t("3 Columns"),
                onActivate: () => {
                    this._updateCols(3)
                },
                closeOnActivate: true
            },
            {
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1z"/><path d="M12 3v18"/><path d="M13.5 18l2.5 -2.5 -2.5 -2.5"/><path d="M16 15h-6a3 3 0 1 1 0 -6h1"/></svg>`,
                title: this.api.i18n.t("Roll Columns"),
                onActivate: () => {
                    this._rollColumns()
                }
            },
            {type: "separator"},
            {
                type: "html",
                title: this.api.i18n.t("Column Gap"),
                element: this.createNumberInput("gap", this.api.i18n.t("Column Gap"), "px", this._getDefaultGap, { min: 0, placeholder: this._getDefaultGap })
            }
        ];

        columnSettings.push({
            type: "html",
            title: this.api.i18n.t("Column Ratio"),
            element: this.createRatioInputs()
        });

        columnSettings.push({type: "separator"});
        columnSettings.push({
            icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7l16 0" /><path d="M10 11l0 6" /><path d="M14 11l0 6" /><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" /><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" /></svg>`,
            title: this.api.i18n.t("Reset"),
            hint: {title: "이 레이아웃의 스타일을 초기화합니다."},
            closeOnActivate: true,
            onActivate: () => {
                this.resetStyle();
            }
        })

        return columnSettings;
    }

    createRatioInputs() {
        const container = make('div', 'maily-editor-utils--multiple-input-container');
        const labelEl = make('label', 'maily-editor-utils--multiple-input-label', {textContent: this.api.i18n.t("Column Ratio")});

        const ratioInputs = [];

        // 해당 열 수용 아이콘 배열 가져오기
        for (let i = 0; i < this.editors.numberOfColumns; i++) {
            ratioInputs.push(this.createRatioInput(i, this.api.i18n.t(`Column Ratio ${i}`), this.editors.numberOfColumns === 1))
        }

        container.append(labelEl, ...ratioInputs);

        return container
    }

    createRatioInput(key, label, disabled = false) {
        // 인풋: 너비, 패딩, 테두리, 포커스 스타일 등 지정
        const inputEl = make('input', 'maily-editor-utils--multiple-input-default', {
            type: 'number',
            step: '1',
            value: this.data.ratios[key],
            placeholder: '1',
            min: 1,
            disabled: disabled
        });

        inputEl.addEventListener('input', () => {
            this.data.ratios[key] = inputEl.value;
            this._updateLayout();
        });

        return inputEl;
    }

    createNumberInput(key, label, unit, defaultValue, attributes = {}) {
        // 컨테이너: 상대 위치 지정 및 위쪽 마진 추가
        const container = make('div', 'maily-editor-utils--input-container');
        // 라벨: 절대 위치, 배경색, 패딩, 폰트 스타일 지정
        const labelEl = make('label', 'maily-editor-utils--input-label', {textContent: `${label} (${unit})`});
        // 인풋: 너비, 패딩, 테두리, 포커스 스타일 등 지정
        const inputEl = make('input', 'maily-editor-utils--input-default', {
            type: 'number',
            step: unit === 'rem' ? '0.5' : '1',
            value: this.data[key],
            placeholder: defaultValue,
            ...attributes
        });

        inputEl.addEventListener('input', () => {
            const value = inputEl.value;
            this.data[key] = value === '' ? defaultValue : parseFloat(value);

            this._updateLayout();
        });

        container.append(labelEl, inputEl);
        return container;
    }

    _rollColumns() {
        this.data.cols.unshift(this.data.cols.pop());
        this.editors.cols.unshift(this.editors.cols.pop());
        this._rerender();
    }

    async _updateCols(num) {
        const currentCols = this.editors.numberOfColumns;

        if (num === currentCols) {
            return;
        }

        if (num < currentCols) {
            const swalTailwind = Swal.mixin({
                customClass: {
                    popup: "py-12 bg-white dark:bg-stone-800",
                    title: "text-2xl font-bold text-black dark:text-white",
                    htmlContainer: "text-base py-16",
                    icon: "border-0 mt-0",
                    confirmButton: "noon-red-button mr-3",
                    cancelButton: "noon-white-button"
                },
                buttonsStyling: false
            });

            const result = await swalTailwind.fire({
                title: this.api.i18n.t("Are you sure?"),
                text: this.api.i18n.t(`This will delete last ${currentCols - num} Column!`),
                iconHtml: "⚠️",
                showCancelButton: true,
                cancelButtonText: this.api.i18n.t("Cancel"),
                confirmButtonText: this.api.i18n.t("Yes, delete it!"),
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        // [수정] 컬럼 수를 늘릴 때, 빈 데이터 객체를 미리 추가해줍니다.
        // 기존 코드에서는 이 로직이 if (num < currentCols) 블록 안에 잘못 위치해 있었습니다.
        while (this.data.cols.length < num) {
            this.data.cols.push({ blocks: [] });
        }

        // [핵심 수정] 새로운 컬럼 수에 맞게 데이터 배열의 길이를 조정합니다.
        this.data.cols.length = num;

        // ✨ [가장 중요한 수정] 새로운 컬럼 수에 맞는 기본 비율로 ratios를 재설정합니다.
        this.data.ratios = this._getDefaultLayout(num);

        // [수정] 내부 상태(numberOfColumns)를 갱신합니다.
        this.editors.numberOfColumns = num;

        // [수정] 에디터 인스턴스 배열의 길이를 조정합니다. (rerender에서 어차피 초기화되지만 명시적으로 관리)
        this.editors.cols.length = num;

        // 변경된 사항을 화면에 다시 그립니다.
        this._rerender();
    }

    _createColumnLayout() {
        this.colWrapper.innerHTML = ''; // 기존 컬럼들을 깨끗하게 비웁니다.

        for (let index = 0; index < this.editors.numberOfColumns; index++) {
            const col = make("div", ["ce-columns__col", `ce-columns__col--${index}`], {
                id: `${this.blockId}-${index}`
            });
            this.colWrapper.appendChild(col);
        }

        this._applyLayout();
    }

    // ✨ [핵심 수정 2] 에디터 인스턴스를 초기화하는 로직을 별도 메서드로 분리합니다.
    _initializeEditors() {
        for (let index = 0; index < this.editors.numberOfColumns; index++) {
            const holderId = `${this.blockId}-${index}`;
            // 이 시점에는 holderId를 가진 요소가 DOM에 존재하므로 오류가 발생하지 않습니다.
            const columnData = this.data.cols[index] || {};

            const editorjs_instance = new this.config.EditorJsLibrary({
                defaultBlock: "paragraph",
                holder: holderId, // `col.id` 대신 생성된 ID를 사용합니다.
                tools: this.config.tools,
                tunes: this.config.tunes,
                data: {blocks: columnData.blocks || []},
                readOnly: this.readOnly,
                minHeight: 50,
                onReady: () => {
                    const editorWrapper = editorjs_instance.ui.nodes.wrapper;
                    if (!editorWrapper) {
                        console.error("Editor.js instance wrapper not found.");
                        return;
                    }
                    const keydownHandler = (event) => this.onKeyDownColumnEditor(event);
                    this.keydownHandlers.set(editorjs_instance, keydownHandler);
                    editorWrapper.addEventListener('keydown', keydownHandler);

                    if (!this.readOnly && this.isNewBlock && index === 0) {
                        editorjs_instance.caret.focus(true);
                    }
                    if (this.config.registerUndoManager) {
                        this.config.registerUndoManager(this.blockId, index, editorjs_instance);
                    }
                },
            });

            this.editors.cols[index] = editorjs_instance;
        }
    }

    onKeyDownColumnEditor = (event) => {
        stopPropagationOnEditorJsControlKeys(event);

        // Enter, Tab, 단축키 처리
        if (event.key === "Enter" || event.key === "Tab") {
            // event.preventDefault();
            event.stopPropagation();
        }

        if (event.metaKey || event.ctrlKey) {
            const isSaveShortcut = event.key.toLowerCase() === 's';
            const isSlash = event.key.toLowerCase() === '/';
            const isUndo = event.key.toLowerCase() === 'z';
            const isRedo = (event.key.toLowerCase() === 'z' && event.shiftKey) || event.key.toLowerCase() === 'y';

            if (!isSaveShortcut && !isSlash && !isUndo && !isRedo) {
                event.stopPropagation();
            }
        }
    }

    _applyLayout() {
        // ✨ [수정] colWrapper는 이제 스타일 래퍼가 아니므로 여기에 gap을 직접 적용하지 않습니다.
        // 대신 grid-template-columns와 gap은 BlockStyleTune이 적용한 래퍼의 하위 요소인
        // ce-columns에 적용됩니다. (CSS에서 처리하는 것이 더 좋을 수 있습니다)
        // 여기서는 grid 레이아웃을 직접 제어합니다.
        this.colWrapper.style.display = 'grid';
        this.colWrapper.style.gap = this.editors.numberOfColumns > 1 ? `${this.data.gap}px` : '0px';

        const ratios = this.data.ratios.map(r => `${r}fr`).join(' ');
        this.colWrapper.style.gridTemplateColumns = ratios;
    }

    resetStyle() {
        this.data.gap = this._getDefaultGap;
        this.data.ratios = this._getDefaultLayout(this.editors.numberOfColumns);
        this._rerender();
    }

    async _updateLayout() {
        await this.save(); // 변경된 데이터 저장 요청

        this._applyLayout();
    }

    async _rerender() {
        await this.save();

        this._destroyAllEditors();

        this._createColumnLayout();

        setTimeout(() => {
            // rerender 과정에서 블록이 제거되었을 수 있으므로 안전장치를 추가합니다.
            if (this.colWrapper && this.colWrapper.isConnected) {
                this._initializeEditors();
            }
        }, 0);
    }

    render() {
        this.colWrapper = document.createElement("div");
        this.colWrapper.classList.add("ce-columns");

        this._destroyAllEditors();

        this._createColumnLayout();

        // 2. 에디터 초기화는 다음 이벤트 루프 틱으로 연기합니다.
        //    이 시점이 되면 this.colWrapper가 메인 DOM에 추가된 후입니다.
        setTimeout(() => {
            // 블록이 DOM에 여전히 연결되어 있는지 확인하는 것이 안전합니다.
            if (this.colWrapper && this.colWrapper.isConnected) {
                this._initializeEditors();
            }
        }, 0); // 0ms 지연으로도 충분합니다.

        return this.colWrapper;
    }

    async save() {
        if (!this.readOnly) {
            for (let index = 0; index < this.editors.numberOfColumns; index++) {
                if (this.editors.cols[index] && typeof this.editors.cols[index].save === 'function') {
                    try {
                        const savedBlocks = await this.editors.cols[index].save();
                        // this.data.cols가 줄어든 경우를 대비하여 인덱스 존재 여부 확인
                        if (this.data.cols[index]) {
                            this.data.cols[index].blocks = savedBlocks.blocks;
                        } else {
                            // 이런 경우는 컬럼 수가 줄어드는 도중에 save가 호출된 경우일 수 있습니다.
                            // 데이터 구조 일관성을 위해 빈 블록으로라도 채워넣거나,
                            // 데이터 배열 길이를 먼저 조정하는 것이 좋습니다.
                            // _updateCols에서 이미 data.cols.length를 조정했으므로 이 경우는 드뭅니다.
                        }
                    } catch (e) {
                        console.error(`Error saving column ${index}:`, e);
                    }
                } else {
                    // 에디터가 아직 생성되지 않은 경우(예: rerender 직후)
                    // 기존 데이터를 유지하거나 빈 블록으로 설정합니다.
                    // 현재 구조에서는 _updateCols에서 데이터가 이미 관리되므로 이 부분은 안전망 역할입니다.
                    if (!this.data.cols[index]) {
                        this.data.cols[index] = { blocks: [] };
                    }
                }
            }
        }

        // data.cols가 비어있지 않도록 보장
        this.data.cols = this.data.cols.slice(0, this.editors.numberOfColumns);
        return this.data;
    }

    static get toolbox() {
        return {
            // UPDATED: 새로운 아이콘과 이름
            icon: `<svg width="14" height="14" viewBox="0 -1 14 14" xmlns="http://www.w3.org/2000/svg">
    <defs></defs>
    <rect x="1.194" y="-0.041" width="11.601" height="12.11" rx="1" ry="1" style="fill: rgb(255, 255, 255); stroke: rgb(0, 0, 0); stroke-width: 1.5px;"></rect>
    <path style="stroke: rgb(0, 0, 0); stroke-width:1.5px" d="M 7.032 -0.034 L 6.948 11.842"></path>
  </svg>`,
            title: "Columns",
        };
    }
}